import { DateTime } from 'luxon';
import { SubjectData } from 'shell/models/person';
import { AccordionCohabitationDeFacto as CohabitationDeFactoVM } from 'shell/view-models/accordion-cohabitation-de-facto';
import { transformToChoiceDate } from 'shell/components/shared/choice-date/functions';

export const transformCohabitationDeFacto = (
  person: SubjectData
): CohabitationDeFactoVM | undefined => {
  const birthChoiceDate = transformToChoiceDate(
    person.convivenzaDiFatto?.convivente?.senzaGiorno,
    person.convivenzaDiFatto?.convivente?.senzaGiornoMese
  );

  const result: CohabitationDeFactoVM | undefined = person.convivenzaDiFatto
    ? {
        orderCoexistence: person.convivenzaDiFatto?.ordineConvivenza || '',
        person: {
          fiscalCode: person.convivenzaDiFatto?.convivente?.codiceFiscale?.codFiscale || '',
          name: person.convivenzaDiFatto?.convivente?.nome || '',
          surname: person.convivenzaDiFatto?.convivente?.cognome || '',
          withoutName: person.convivenzaDiFatto?.convivente?.senzaNome === '1' ? true : false,
          withoutSurname: person.convivenzaDiFatto?.convivente?.senzaCognome === '1' ? true : false,
        },
        generality: {
          aire: person.convivenzaDiFatto?.convivente?.soggettoAIRE || '',
          validityFiscalCode: person.convivenzaDiFatto?.convivente?.codiceFiscale?.validitaCF || '',
          validityDateFiscalCode: person.convivenzaDiFatto?.convivente?.codiceFiscale
            ?.dataAttribuzioneValidita
            ? DateTime.fromISO(
                person.convivenzaDiFatto?.convivente?.codiceFiscale?.dataAttribuzioneValidita
              )
            : null,
          gender: person.convivenzaDiFatto?.convivente?.sesso || '',
          expatriationYear: person.convivenzaDiFatto?.convivente?.annoEspatrio || '',
          birthDate: person.convivenzaDiFatto?.convivente?.dataNascita
            ? DateTime.fromISO(person.convivenzaDiFatto?.convivente?.dataNascita)
            : null,
          // senza giorno mese, etc
          birthChoiceDate,
          // generality italian form
          italian: {
            location:
              person.convivenzaDiFatto?.convivente?.luogoNascita?.localita?.descrizioneLocalita ||
              '',
            province:
              person.convivenzaDiFatto?.convivente?.luogoNascita?.comune?.siglaProvinciaIstat || '',
            municipality: person.convivenzaDiFatto?.convivente?.luogoNascita?.comune
              ? {
                  description:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.comune?.nomeComune || '',
                  province:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.comune
                      ?.siglaProvinciaIstat || '',
                }
              : null,
          },
          isForeign: !!person.convivenzaDiFatto?.convivente?.luogoNascita?.localita,
          // generality foreign form
          foreign: {
            state: person.convivenzaDiFatto?.convivente?.luogoNascita?.localita
              ? {
                  id:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.localita?.codiceStato || '',
                  value:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.localita
                      ?.descrizioneStato || '',
                }
              : null,
            province:
              person.convivenzaDiFatto?.convivente?.luogoNascita?.localita?.provinciaContea || '',
            municipality: person.convivenzaDiFatto?.convivente?.luogoNascita?.localita
              ? {
                  description:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.localita
                      ?.descrizioneLocalita || '',
                  province:
                    person.convivenzaDiFatto?.convivente?.luogoNascita?.localita?.provinciaContea ||
                    '',
                }
              : null,
          },
          // Other fields generality
          exceptionalBirthPlace:
            person.convivenzaDiFatto?.convivente?.luogoNascita?.luogoEccezionale || '',
          municipalityIdentifier:
            person.convivenzaDiFatto?.convivente?.idSchedaSoggettoComune?.idSchedaSoggetto || '',
          istatCode:
            person.convivenzaDiFatto?.convivente?.idSchedaSoggettoComune
              ?.idSchedaSoggettoComuneIstat || '',
          anprIdentifier: person.convivenzaDiFatto?.convivente?.idSchedaSoggettoANPR || '',
          note: person.convivenzaDiFatto?.convivente?.note || '',
        },
        citizenshipCode: person.convivenzaDiFatto?.cittadinanza?.codiceStato || '',
        citizenship: person.convivenzaDiFatto?.cittadinanza
          ? {
              value: person.convivenzaDiFatto?.cittadinanza?.descrizioneStato || '',
              id: person.convivenzaDiFatto?.cittadinanza?.codiceStato || '',
            }
          : null,
        fromCitizenship: person.convivenzaDiFatto?.cittadinanza?.dataValidita
          ? DateTime.fromISO(person.convivenzaDiFatto?.cittadinanza?.dataValidita)
          : null,
        noteCitizenship: person.convivenzaDiFatto?.cittadinanza?.noteStato || '',
        contractStipulation: {
          documentData: person.convivenzaDiFatto?.contrattoStipula?.documento || null,
          protocol: person.convivenzaDiFatto?.contrattoStipula?.protocollo,
          dateStart: person.convivenzaDiFatto?.contrattoStipula?.data
            ? DateTime.fromISO(person.convivenzaDiFatto?.contrattoStipula?.data)
            : null,
          dateNotification: person.convivenzaDiFatto?.contrattoStipula?.dataNotifica
            ? DateTime.fromISO(person.convivenzaDiFatto?.contrattoStipula?.dataNotifica)
            : null,
          dateDocument: person.convivenzaDiFatto?.contrattoStipula?.dataDocumento
            ? DateTime.fromISO(person.convivenzaDiFatto?.contrattoStipula?.dataDocumento)
            : null,
          professional: person.convivenzaDiFatto?.contrattoStipula?.professionista || '',
          typeProfession:
            person.convivenzaDiFatto?.contrattoStipula?.professionista?.charAt(0) || '',
          municipality: person.convivenzaDiFatto?.contrattoStipula?.comuneRegistrazione
            ? {
                description:
                  person.convivenzaDiFatto?.contrattoStipula?.comuneRegistrazione?.nomeComune || '',
                province:
                  person.convivenzaDiFatto?.contrattoStipula?.comuneRegistrazione
                    ?.siglaProvinciaIstat || '',
              }
            : null,
          provinceMunicipality:
            person.convivenzaDiFatto?.contrattoStipula?.comuneRegistrazione?.siglaProvinciaIstat ||
            '',
          locationMunicipality:
            person.convivenzaDiFatto?.contrattoStipula?.comuneRegistrazione?.descrizioneLocalita ||
            '',
          municipalityPlace: person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.comune
            ? {
                id:
                  person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.comune?.codiceIstat ||
                  '',
                description:
                  person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.comune?.nomeComune || '',
                province:
                  person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.comune
                    ?.siglaProvinciaIstat || '',
              }
            : null,
          provincePlace:
            person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.comune?.siglaProvinciaIstat ||
            '',
          locationPlace:
            person.convivenzaDiFatto?.contrattoStipula?.luogoEvento?.localita
              ?.descrizioneLocalita || '',
        },
        contractTermination: {
          documentData:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.documento || null,
          protocol: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.protocollo || '',
          dateStart: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.data
            ? DateTime.fromISO(person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.data)
            : null,
          dateNotification: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.dataNotifica
            ? DateTime.fromISO(
                person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.dataNotifica
              )
            : null,
          dateDocument: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.dataDocumento
            ? DateTime.fromISO(
                person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.dataDocumento
              )
            : null,
          typeProfession:
            person.convivenzaDiFatto?.contrattoStipula?.professionista?.charAt(0) || '',
          professional:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.professionista || '',
          municipality: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto
            ? {
                description:
                  person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.comuneRegistrazione
                    ?.nomeComune || '',
                province:
                  person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.comuneRegistrazione
                    ?.siglaProvinciaIstat || '',
              }
            : null,
          // municipality:
          //   person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.comuneRegistrazione
          //     ?.nomeComune || '',
          provinceMunicipality:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.comuneRegistrazione
              ?.siglaProvinciaIstat || '',
          locationMunicipality:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.comuneRegistrazione
              ?.descrizioneLocalita || '',
          municipalityPlace: person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento
            ?.comune?.codiceIstat
            ? {
                id:
                  person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento?.comune
                    ?.codiceIstat || '',
                description:
                  person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento?.comune
                    ?.nomeComune || '',
                province:
                  person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento?.comune
                    ?.siglaProvinciaIstat || '',
              }
            : null,
          provincePlace:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento?.comune
              ?.siglaProvinciaIstat || '',
          locationPlace:
            person.convivenzaDiFatto?.contrattoRisoluzione?.contratto?.luogoEvento?.localita
              ?.descrizioneLocalita || '',
        },
        reasonTermination:
          person.convivenzaDiFatto?.contrattoRisoluzione?.motivoRisoluzione || undefined,
      }
    : undefined;
  return result;
};
